import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSalon } from '../../contexts/SalonContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';

// Importar as funções centralizadas de appointments
import { getAppointments } from '../../firebase';

/**
 * Página de agendamentos do profissional
 */
const ProfessionalAgendamentosPage = () => {
  const { selectedSalonId } = useUser(); // Obter salonId do contexto do usuário
  const { currentUser } = useAuth();
  const { salonConfig } = useSalon();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professionalId, setProfessionalId] = useState(null);
  const [filter, setFilter] = useState('proximos'); // proximos, anteriores, todos
  
  // Carregar dados do profissional e agendamentos
  useEffect(() => {
    const loadData = async () => {
      if (!selectedSalonId || !currentUser) {
        return;
      }
      
      setLoading(true);
      try {
        // 1. Buscar ID do profissional
        const profQuery = query(
          collection(db, 'salons', selectedSalonId, 'professionals'),
          where('userId', '==', currentUser.uid)
        );
        
        const profSnapshot = await getDocs(profQuery);
        if (profSnapshot.empty) {
          setError('Profissional não encontrado');
          setLoading(false);
          return;
        }
        
        const profId = profSnapshot.docs[0].id;
        setProfessionalId(profId);
        
        // 2. Buscar agendamentos usando a função centralizada
        try {
          const now = new Date();
          const today = format(now, 'yyyy-MM-dd');
          
          // Filtros base (sempre incluir o ID do profissional)
          const filters = { 
            professionalId: profId,
            // Usar formato de string para data, que é como está armazenado na maioria dos agendamentos
            stringDateFormat: true 
          };
          
          // Adicionar filtros baseados na seleção do usuário
          if (filter === 'proximos') {
            // Filtrar agendamentos a partir de hoje
            filters.startDate = today;
          } else if (filter === 'anteriores') {
            // Filtrar agendamentos até ontem
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            filters.endDate = format(yesterday, 'yyyy-MM-dd');
          }
          // Para 'todos', não adicionar filtros de data
          
          console.log(`[ProfessionalAgendamentosPage] Buscando agendamentos com filtro: ${filter}`, filters);
          
          const appointmentsData = await getAppointments(selectedSalonId, filters);
          console.log(`[ProfessionalAgendamentosPage] Encontrados ${appointmentsData.length} agendamentos`);
          
          // Verificar horários para filtrar ainda mais os resultados
          let filteredAppointments = appointmentsData;
          
          if (filter === 'proximos') {
            // Para próximos, filtrar apenas horários no futuro
            filteredAppointments = appointmentsData.filter(apt => {
              // Criar um objeto Date completo com data e hora
              const aptDateTime = getAppointmentDateTime(apt);
              return aptDateTime >= now;
            });
          } else if (filter === 'anteriores') {
            // Para anteriores, filtrar apenas horários no passado
            filteredAppointments = appointmentsData.filter(apt => {
              // Criar um objeto Date completo com data e hora
              const aptDateTime = getAppointmentDateTime(apt);
              return aptDateTime < now;
            });
          }
          
          console.log(`[ProfessionalAgendamentosPage] Após filtro de horário: ${filteredAppointments.length} agendamentos`);
          
          // Ordenar os agendamentos com base no filtro
          let sortedAppointments = [...filteredAppointments];
          if (filter === 'proximos') {
            // Ordenar por data crescente (próximos primeiro)
            sortedAppointments.sort((a, b) => {
              const dateA = getAppointmentDateTime(a);
              const dateB = getAppointmentDateTime(b);
              return dateA - dateB;
            });
          } else {
            // Ordenar por data decrescente (mais recentes primeiro)
            sortedAppointments.sort((a, b) => {
              const dateA = getAppointmentDateTime(a);
              const dateB = getAppointmentDateTime(b);
              return dateB - dateA;
            });
          }
          
          // Garantir que startTime e endTime sejam objetos Date para exibição
          sortedAppointments = sortedAppointments.map(apt => {
            const startTime = getAppointmentDateTime(apt);
            
            // Calcular endTime com base na duração
            const endTime = new Date(startTime);
            endTime.setMinutes(endTime.getMinutes() + (apt.duracaoTotal || 30));
            
            return {
              ...apt,
              startTime,
              endTime
            };
          });
          
          setAppointments(sortedAppointments);
          setError(null);
        } catch (appErr) {
          console.error('Erro ao carregar agendamentos:', appErr);
          setError('Erro ao carregar agendamentos: ' + appErr.message);
        }
        
      } catch (err) {
        console.error('Erro ao carregar agendamentos:', err);
        setError('Erro ao carregar agendamentos: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedSalonId, currentUser, filter]);
  
  // Função utilitária para obter um objeto Date completo a partir do agendamento
  const getAppointmentDateTime = (appointment) => {
    // 1. Verificar se temos dataTimestamp (Timestamp do Firestore)
    if (appointment.dataTimestamp && typeof appointment.dataTimestamp.toDate === 'function') {
      return appointment.dataTimestamp.toDate();
    }
    
    // 2. Verificar se temos startTime como Timestamp do Firestore
    if (appointment.startTime && typeof appointment.startTime.toDate === 'function') {
      return appointment.startTime.toDate();
    }
    
    // 3. Verificar se temos startTime como Date
    if (appointment.startTime instanceof Date) {
      return appointment.startTime;
    }
    
    // 4. Verificar se temos data como string e horário
    if (appointment.data && appointment.horario) {
      // Se a data estiver no formato YYYY-MM-DD
      if (appointment.data.includes('-')) {
        const [year, month, day] = appointment.data.split('-').map(Number);
        const [hours, minutes] = appointment.horario.split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
      }
      
      // Se a data estiver no formato DD/MM/YYYY
      if (appointment.data.includes('/')) {
        const [day, month, year] = appointment.data.split('/').map(Number);
        const [hours, minutes] = appointment.horario.split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
      }
    }
    
    // 5. Fallback: retornar data atual (não deveria acontecer, mas é uma proteção)
    console.warn('Não foi possível determinar a data/hora do agendamento:', appointment);
    return new Date();
  };
  
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };
  
  // Renderizar estado de carregamento
  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }
  
  // Renderizar erro
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Meus Agendamentos</h1>
      
      {/* Filtros */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-4">
          <button
            className={`pb-3 px-1 ${filter === 'proximos' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handleFilterChange('proximos')}
          >
            Próximos
          </button>
          <button
            className={`pb-3 px-1 ${filter === 'anteriores' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handleFilterChange('anteriores')}
          >
            Anteriores
          </button>
          <button
            className={`pb-3 px-1 ${filter === 'todos' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handleFilterChange('todos')}
          >
            Todos
          </button>
        </div>
      </div>
      
      {/* Lista de agendamentos */}
      {appointments.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">Nenhum agendamento encontrado</p>
        </div>
      ) : (
        <div className="space-y-4">
          {appointments.map(appointment => {
            const isPast = new Date(appointment.startTime) < new Date();
            const isToday = new Date(appointment.startTime).toDateString() === new Date().toDateString();
            
            return (
              <div 
                key={appointment.id} 
                className={`p-4 rounded-lg border ${isToday ? 'bg-blue-50 border-blue-200' : isPast ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
              >
                <div className="flex justify-between flex-wrap">
                  <div>
                    <h3 className="font-medium text-gray-900">{appointment.clientName || appointment.cliente}</h3>
                    <p className="text-sm text-gray-500">
                      {Array.isArray(appointment.servicos) && appointment.servicos.length > 0 
                        ? appointment.servicos.map(s => s.nome).join(', ')
                        : Array.isArray(appointment.services) && appointment.services.length > 0 
                        ? appointment.services[0].name 
                        : appointment.serviceName || 'Serviço'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {appointment.startTime.toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long'
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {appointment.startTime.toLocaleTimeString('pt-BR', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      })} - {appointment.endTime.toLocaleTimeString('pt-BR', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="mt-2 flex justify-between items-center">
                  <span 
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${appointment.status === 'confirmed' || appointment.confirmado ? 'bg-green-100 text-green-800' : 
                      appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      appointment.status === 'cancelled' || appointment.cancelado ? 'bg-red-100 text-red-800' :
                      appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'}`}
                  >
                    {appointment.status === 'confirmed' || appointment.confirmado ? 'Confirmado' :
                     appointment.status === 'pending' ? 'Pendente' :
                     appointment.status === 'cancelled' || appointment.cancelado ? 'Cancelado' :
                     appointment.status === 'completed' ? 'Concluído' :
                     appointment.status || 'Agendado'}
                  </span>
                  
                  <div className="flex items-center space-x-4">
                    {(appointment.comissao !== undefined || (Array.isArray(appointment.servicos) && appointment.servicos.some(s => s.comissaoProfissional))) && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Comissão</p>
                        <p className="text-sm font-medium text-green-600">
                          R$ {(
                            appointment.comissao || 
                            (Array.isArray(appointment.servicos) ? 
                              appointment.servicos.reduce((acc, s) => acc + (s.comissaoProfissional || 0), 0) : 0)
                          ).toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Valor</p>
                      <p className="text-sm font-medium text-gray-900">
                        R$ {(appointment.valorTotal || appointment.price || 0).toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfessionalAgendamentosPage; 